<template>
    <Form
        v-slot="{ handleSubmit }"
        as=""
        keep-values
        :validation-schema="formSchema"
    >
        <Dialog v-model:open="opened">
            <DialogTrigger>
                <slot />
            </DialogTrigger>
            <DialogContent>
                <DialogHeader class="items-center">
                    <template #icon> <StripeLogo /></template>
                    <DialogTitle>
                        {{
                            integration
                                ? $t("integrations.stripe.modal.title_edit")
                                : $t("integrations.stripe.modal.title_add")
                        }}</DialogTitle
                    >
                </DialogHeader>
                <form
                    id="stripe-form"
                    @submit="handleSubmit($event, addStripeIntegration)"
                    class="px-6 pt-4"
                >
                    <FormField
                        v-auto-animate
                        v-slot="{ componentField }"
                        name="name"
                    >
                        <FormItem v-auto-animate>
                            <FormLabel>{{
                                $t("integrations.stripe.modal.name")
                            }}</FormLabel>
                            <FormControl>
                                <Input
                                    :disabled="loading"
                                    v-model="integrationName"
                                    type="text"
                                    v-bind="componentField"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>

                    <Alert>
                        <p
                            v-html="
                                $t('integrations.stripe.modal.redirect_note')
                            "
                        ></p>
                    </Alert>
                </form>

                <DialogFooter
                    class="w-full"
                    :class="cn(removable ? '!justify-between' : 'justify-end')"
                >
                    <Button
                        v-if="removable"
                        @click="$emit('remove-integration')"
                        variant="destructive-ghost"
                        class="gap-1 flex"
                        size="xs"
                    >
                        <Trash :size="16" />
                        {{ $t("integrations.remove_integration") }}
                    </Button>

                    <div class="flex-end flex">
                        <DialogClose>
                            <Button
                                class="ml-4"
                                :disabled="loading"
                                variant="outline"
                                type="button"
                                >{{ $t("cancel") }}</Button
                            >
                        </DialogClose>

                        <Button
                            class="ml-4"
                            type="submit"
                            :disabled="loading"
                            form="stripe-form"
                            v-auto-animate
                        >
                            <svg
                                v-if="loading"
                                class="-ml-1 mr-3 size-5 animate-spin text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    class="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    stroke-width="4"
                                ></circle>
                                <path
                                    class="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            {{ $t("continue") }}</Button
                        >
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </Form>
</template>

<script setup lang="ts">
import { cn } from "@/lib/utils";
import { ref } from "vue";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose,
} from "@/components/ui/dialog-v2";
import Button from "@/components/ui/button/Button.vue";
import { Trash } from "lucide-vue-next";
import { Input } from "@/components/ui/input";
import StripeLogo from "../../logos/simple/Stripe.svg";
import axios from "axios";
import route from "ziggy-js";
import { Alert } from "@/components/ui/alert";
import { vAutoAnimate } from "@formkit/auto-animate/vue";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { toTypedSchema } from "@vee-validate/zod";
import { z } from "zod";

const schema = z.object({
    name: z.string().min(1).max(50),
});

const formSchema = toTypedSchema(schema);

type Integration = {
    name: string;
    type: string;
    id: number;
    data: any;
    errors: any[];
};

const props = defineProps<{
    removable?: boolean;
    integration?: Integration;
}>();

const integrationName = ref(props.integration ? props.integration.name : "");
const opened = ref(false);
const loading = ref(false);

async function addStripeIntegration(values: any) {
    if (loading.value) return;

    loading.value = true;

    const response = await axios.post(route("app.stripe-integrations.store"), {
        name: values.name,
    });

    if (response.data.success) {
        window.location.href = response.data.data.url;
    } else {
        loading.value = false;
    }
}
</script>
